import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import * as Sentry from '@sentry/browser'
import type { BrowserOptions, Event, EventHint } from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig, sentryDefaults as zionSentryDefaults } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'

import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

const debug = zionDebug('church:index')

const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

const treeSentryDefaults: Partial<BrowserOptions> = {
  ignoreErrors: [
    /^.*-extension:\/\/.*$/,
    /^.*AbortError: The transaction was aborted.*$/,
    /^.*AbortError: Version change transaction was aborted.*$/,
    /^.*Blocked a frame with origin.*$/,
    /^.*Can't find variable.*$/,
    /^.*Connection is closing.*$/,
    /^.*Connection on cached page closed.*$/,
    /^.*connection was closed.*$/,
    /^.*ChunkLoadError.*$/,
    /^.*Database deleted.*$/,
    /^.*database is a higher version.*$/,
    /^.*database using a lower version.*$/,
    /^.*disk is full.*$/,
    /^.*Error creating or migrating .* in database.*$/,
    /^.*Error looking up record in object store.*$/,
    /^.*Error:.*Indexed Database server.*$/,
    /^.*Extension context invalidated.*$/,
    /^.*Failed to execute 'replaceState' on 'History'.*$/,
    /^.*flatMap.*$/,
    /^.*Internal error retrieving bucket.*$/,
    /^.*is not defined.*$/,
    /^.*Java object is gone.*$/,
    /^.*Loading CSS chunk.*$/,
    /^.*Munchkin.*$/,
    /^.*Network Error.*$/,
    /^.*NetworkError when attempting to fetch resource.*$/,
    /^.*No Session Id.*$/,
    /^.*no user signed in.*$/,
    /^.*Non-Error promise rejection captured.*$/,
    /^.*One of the specified object stores was not found.*$/,
    /^.*QuotaExceededError.*$/,
    /^.*redefine .*property.*$/,
    /^.*Request aborted.*$/,
    /^.*ResizeObserver loop limit exceeded.*$/,
    /^.*SecurityError: The operation is insecure.*$/,
    /^.*status.*401.*$/,
    /^.*status.*403.*$/,
    /^.*status.*404.*$/,
    /^.*status.*410.*$/,
    /^.*status.*422.*$/,
    /^.*The operation failed for reasons unrelated to the database itself.*$/,
    /^.*The requested version.*$/,
    /^.*The user denied permission to access the database.*$/,
    /^.*Transaction timed out due to inactivity.*$/,
    /^.*Unable to open database.*$/,
    /^.*DataError: Failed to read large IndexedDB value.*$/,
    /^.*window\.onunload is not a function.*$/,
    /^.*is not a child of this node.*$/,
    /^.*Attempt to get a record from database without an in-progress transaction.*$/,
  ],
  denyUrls: [/^.*edge\.fscdn\.org\/assets\/static\/css\/.*$/, /contentsquare\.net/, /consent\.trustarc\.com/],
  allowUrls: [
    /^.*edge\.fscdn\.org.*$/,
    /^.*familysearchcdn\.org.*$/,
    /^https?:\/\/((www|beta|integration)\.)?familysearch\.org.*$/,
  ],
}

const isTrustArcUrl = (value: unknown): boolean => {
  if (typeof value !== 'string' || value.length === 0) {
    return false
  }

  // Normalize bare hostnames (e.g. "consent.trustarc.com") so `new URL` parses
  // them as the host instead of treating them as a relative path.
  const candidate = /^[a-z][a-z0-9+\-.]*:/i.test(value) ? value : `https://${value}`

  try {
    const { hostname } = new URL(candidate)
    return hostname === 'consent.trustarc.com' || hostname.endsWith('.consent.trustarc.com')
  } catch {
    return false
  }
}

if (sentryDSN) {
  Sentry.init(
    mergeSentryConfig({
      ...zionSentryDefaults,
      ...treeSentryDefaults,
      dsn: sentryDSN,
      beforeSend(event: Event, hint?: EventHint): Event | null {
        try {
          const orig = hint?.originalException as { target?: { src?: unknown } } | undefined
          if (isTrustArcUrl(orig?.target?.src)) {
            return null
          }

          const hasTrustArcBreadcrumb = (event.breadcrumbs || []).some((b) => {
            const data = b.data || {}
            const url = data.url || data.request?.url || b.message
            return isTrustArcUrl(url)
          })
          if (hasTrustArcBreadcrumb) {
            return null
          }
        } catch (e) {
          debug('Sentry filter error:', e)
        }
        return event
      },
    })
  )
}

const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here, languages and translations are configured and available here.
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  return (
    <Router basename={base}>
      <Root analytics>
        <AppLayout fullWidth>
          <App />
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element with id "root" was not found in the document.')
}
ReactDOM.render(<FrontierRoot />, rootElement)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
